import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Check, Eye, EyeOff, RefreshCw, KeyRound, Link2, RadioTower, ShieldAlert, Clock3, Store, FileJson, ListChecks, History, Upload, MessageSquareText, ChevronDown, ChevronUp, AlertTriangle, DatabaseBackup } from "lucide-react";
import { SectionHeader, Panel, Callout } from "@/components/dashboard/SectionHeader";
import { FeedPill } from "@/components/dashboard/FeedAlertBanner";
import { StoreFeedDot, silentText } from "@/components/dashboard/FeedAlertBanner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { api, INGEST_URL, PING_URL } from "@/lib/api";
import { cn } from "@/lib/utils";

const copyText = async (text, what = "Copied") => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${what} copied to clipboard`);
  } catch {
    toast.error("Copy failed - select the text and copy it manually");
  }
};

const CopyButton = ({ text, what, testId, size = "sm" }) => {
  const [done, setDone] = useState(false);
  return (
    <Button
      type="button"
      variant="secondary"
      size={size}
      data-testid={testId}
      onClick={async () => {
        await copyText(text, what);
        setDone(true);
        setTimeout(() => setDone(false), 1500);
      }}
      className="bg-secondary text-foreground border border-border/70 hover:bg-secondary/80 rounded-md h-8 px-2.5 gap-1.5 text-xs"
    >
      {done ? <Check size={13} className="text-[#2FBF8F]" /> : <Copy size={13} />}
      {done ? "Copied" : "Copy"}
    </Button>
  );
};

const Code = ({ children, className, testId }) => (
  <pre data-testid={testId} className={cn("font-mono text-[12px] leading-relaxed rounded-lg border border-border/60 bg-[#0b0c0e]/80 p-3 overflow-x-auto scroll-thin whitespace-pre-wrap break-all text-[#E8E2D4]", className)}>
    {children}
  </pre>
);

const fmtTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("en-MY", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", timeZone: "Asia/Kuala_Lumpur" }) + " MYT";
};

export default function IntegrationBrief({ feed, onFeedChange, sites = [], onNavigate }) {
  const [brief, setBrief] = useState(null);
  const [deliveries, setDeliveries] = useState(null);
  const [error, setError] = useState(null);
  const [reveal, setReveal] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [reimporting, setReimporting] = useState(false);
  const [promptOpen, setPromptOpen] = useState(false);
  const [promptWithKey, setPromptWithKey] = useState(true);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [b, d, s] = await Promise.all([api.getFeedBrief(), api.getFeedDeliveries(25), api.getFeedStatus()]);
      setBrief(b);
      setDeliveries(d.deliveries || []);
      onFeedChange?.(s);
    } catch (e) {
      console.error(e);
      setError("Could not load the integration brief.");
    }
  }, [onFeedChange]);

  useEffect(() => {
    load();
  }, [load]);

  const rotate = async () => {
    setRotating(true);
    try {
      const info = await api.rotateFeedKey();
      // refresh the whole brief so the curl + handoff prompt carry the new key
      const b = await api.getFeedBrief();
      setBrief(b);
      setReveal(true);
      toast.success("New key generated - the previous key stopped working immediately");
      if (!b?.auth?.key?.key && info?.key) setBrief((cur) => (cur ? { ...cur, auth: { ...cur.auth, key: info } } : cur));
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Could not regenerate the key");
    } finally {
      setRotating(false);
    }
  };

  const reimport = async () => {
    setReimporting(true);
    try {
      const rep = await api.reimportCsv();
      toast.success(`CSV import ran: ${rep.rows} row(s) written from ${rep.files} file(s)`);
      await load();
    } catch (e) {
      console.error(e);
      toast.error("CSV re-import failed");
    } finally {
      setReimporting(false);
    }
  };

  if (error) {
    return (
      <div className="space-y-6">
        <SectionHeader eyebrow="Live Count Feed" title="Integration brief" description={error} />
        <Button variant="secondary" onClick={load} data-testid="brief-retry" className="gap-2"><RefreshCw size={14} /> Retry</Button>
      </div>
    );
  }
  if (!brief) {
    return (
      <div className="space-y-6" data-testid="brief-loading">
        <SectionHeader eyebrow="Live Count Feed" title="Integration brief" description="Loading endpoint, key and delivery log…" />
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
          <Skeleton className="h-56 rounded-xl bg-muted/30 xl:col-span-5" />
          <Skeleton className="h-56 rounded-xl bg-muted/30 xl:col-span-7" />
        </div>
      </div>
    );
  }

  const key = brief.auth?.key || {};
  const keyText = key.key || "";
  const masked = keyText ? `${keyText.slice(0, 9)}${"•".repeat(Math.max(keyText.length - 13, 8))}${keyText.slice(-4)}` : "—";
  const status = feed || brief.status || {};
  const unmapped = status.unmapped || [];
  const importReport = status.import_report;
  const ingestUrl = INGEST_URL || brief.endpoint?.url;
  const pingUrl = PING_URL || brief.ping?.url;
  const curl = `curl -X POST ${ingestUrl} \\\n  -H 'Authorization: Bearer ${keyText}' \\\n  -H 'Content-Type: application/json' \\\n  -d '${JSON.stringify(brief.payload.example_minimal)}'`;
  const exampleMin = JSON.stringify(brief.payload.example_minimal, null, 2);
  const exampleFull = JSON.stringify(brief.payload.example_full, null, 2);
  const handoff = promptWithKey ? brief.handoff_prompt : brief.handoff_prompt_no_key;
  const feedSites = status.sites || [];
  const stopped = feedSites.filter((s) => s.state === "silent" && s.ever_reported);
  const never = feedSites.filter((s) => s.state === "silent" && !s.ever_reported);
  const fullBackfill = brief.backfill?.full || [];
  const continueFrom = brief.backfill?.continue_from || [];

  return (
    <div className="space-y-6 md:space-y-8" data-testid="integration-brief">
      <SectionHeader
        eyebrow="Live Count Feed · Integration Brief"
        title="One endpoint, one key, eight stores."
        description="Hand this page to the project that produces the counts. It posts the running daily totals every 10 minutes; the dashboard stores the latest value per store per day and lights up every page automatically. Nothing on this page is visible without signing in."
        actions={
          <div className="flex items-center gap-2">
            <FeedPill feed={status} onNavigate={() => {}} />
            <Button variant="secondary" data-testid="brief-refresh" onClick={load} className="bg-secondary text-foreground border border-border/70 hover:bg-secondary/80 rounded-lg h-9 gap-2">
              <RefreshCw size={14} /> Refresh
            </Button>
          </div>
        }
      />

      {/* store watch: who is silent right now */}
      {status.state === "silent" && (
        <Callout tone={stopped.length ? "warn" : "info"} testId="brief-silent-summary">
          <div className="flex flex-col md:flex-row md:items-start gap-3">
            <AlertTriangle size={18} className={cn("shrink-0 mt-0.5", stopped.length ? "text-[#E0B84A]" : "text-[#9CCBF0]")} />
            <div className="flex-1">
              <div className="text-sm font-semibold">{status.headline}</div>
              <div className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                Every store is watched during trading hours ({status.trading_hours_myt?.[0]}:00–{status.trading_hours_myt?.[1]}:00 MYT); a store is flagged after {status.silent_after_minutes} minutes without a delivery. It is {status.myt_time} MYT now.
                {stopped.length > 0 && <> Stopped posting: <span className="text-foreground">{stopped.map((s) => `${s.name} (${silentText(s)})`).join(", ")}</span>.</>}
                {never.length > 0 && <> Never connected: {never.map((s) => s.name).join(", ")}.</>}
              </div>
            </div>
          </div>
        </Callout>
      )}

      {/* handoff prompt */}
      <Panel
        index={0}
        testId="brief-handoff-panel"
        title="Hand-off prompt for the counting project"
        subtitle="Paste this into the project that produces the counts. It carries the endpoint, the key, the store ids, the per-store backfill plan and the acceptance test - one shot, nothing else to explain."
        right={<MessageSquareText size={16} className="text-[#C9A961]" />}
      >
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <Button
            type="button"
            data-testid="handoff-copy"
            onClick={() => copyText(handoff, promptWithKey ? "Hand-off prompt (with key)" : "Hand-off prompt (key placeholder)")}
            className="gold-gradient text-[#141821] hover:brightness-[1.04] active:scale-[0.98] rounded-lg h-9 px-4 text-sm font-semibold gap-2 transition-[filter] duration-200"
          >
            <Copy size={14} /> Copy prompt
          </Button>
          <button
            type="button"
            data-testid="handoff-toggle-key"
            onClick={() => setPromptWithKey((v) => !v)}
            className={cn("inline-flex items-center gap-1.5 rounded-md border font-mono text-[11px] py-1.5 px-2.5 focus-gold", promptWithKey ? "border-[rgba(224,184,74,0.35)] bg-[rgba(224,184,74,0.10)] text-[#E0B84A]" : "border-border/70 bg-secondary/60 text-muted-foreground")}
          >
            <KeyRound size={12} /> {promptWithKey ? "includes the live key" : "key left as a placeholder"}
          </button>
          <button
            type="button"
            data-testid="handoff-toggle-preview"
            onClick={() => setPromptOpen((v) => !v)}
            className="inline-flex items-center gap-1.5 rounded-md border border-border/70 bg-secondary/60 font-mono text-[11px] py-1.5 px-2.5 text-muted-foreground hover:text-foreground focus-gold ml-auto"
          >
            {promptOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />} {promptOpen ? "Hide preview" : "Preview prompt"}
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
          <div className="rounded-lg border border-border/60 p-3">
            <div className="eyebrow text-[10px] flex items-center gap-1.5"><DatabaseBackup size={11} /> Full backfill</div>
            <div className="text-sm mt-1" data-testid="handoff-backfill-full">{fullBackfill.length ? fullBackfill.join(", ") : "none - every store has history"}</div>
            <div className="text-[11px] text-muted-foreground mt-1">no CSV history here - the counting project sends every day it has</div>
          </div>
          <div className="rounded-lg border border-border/60 p-3 md:col-span-2">
            <div className="eyebrow text-[10px] flex items-center gap-1.5"><Clock3 size={11} /> Continue from</div>
            <div className="text-sm mt-1 leading-relaxed" data-testid="handoff-backfill-continue">
              {continueFrom.length ? continueFrom.map((c) => `${c.name} from ${c.send_from}`).join(" · ") : "none"}
            </div>
            <div className="text-[11px] text-muted-foreground mt-1">CSV exports stay the current data up to the day before; live rows inside that history are skipped unless replace_history is sent</div>
          </div>
        </div>
        {promptOpen && <Code testId="handoff-preview" className="max-h-[28rem] overflow-y-auto whitespace-pre-wrap break-words">{handoff}</Code>}
      </Panel>

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-4 md:gap-5">
        {/* key */}
        <Panel index={0} className="xl:col-span-5" testId="brief-key-panel" title="Shared live-feed key" subtitle="One key for all eight stores - each row names its store. Treat it like a password." right={<KeyRound size={16} className="text-[#C9A961]" />}>
          <div className="rounded-lg border border-[rgba(201,169,97,0.3)] bg-[rgba(201,169,97,0.06)] p-3">
            <div className="eyebrow text-[10px] mb-1">Authorization: Bearer</div>
            <div className="flex items-center gap-2">
              <code className="font-mono text-[13px] break-all flex-1 text-[#F0DFA8]" data-testid="feed-key-value" data-revealed={reveal ? "true" : "false"}>
                {reveal ? keyText : masked}
              </code>
              <button
                type="button"
                data-testid="feed-key-toggle"
                onClick={() => setReveal((v) => !v)}
                aria-label={reveal ? "Hide key" : "Reveal key"}
                className="h-8 w-8 rounded-md border border-border/70 bg-secondary/60 hover:bg-secondary flex items-center justify-center text-muted-foreground hover:text-foreground focus-gold"
              >
                {reveal ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
              <CopyButton text={keyText} what="Live-feed key" testId="feed-key-copy" />
            </div>
          </div>
          <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
            <div className="text-[11px] font-mono text-muted-foreground" data-testid="feed-key-meta">
              {key.rotated_at ? `Regenerated ${fmtTime(key.rotated_at)} · ${key.rotations} rotation${key.rotations === 1 ? "" : "s"}` : `Created ${fmtTime(key.created_at)}`}
            </div>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" data-testid="feed-key-rotate" disabled={rotating} className="border-[rgba(224,90,79,0.35)] bg-[rgba(224,90,79,0.08)] text-[#F0A29B] hover:bg-[rgba(224,90,79,0.14)] hover:text-[#F7B8B2] rounded-md h-8 px-3 gap-1.5 text-xs">
                  <RefreshCw size={13} className={rotating ? "animate-spin" : ""} /> Regenerate key
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-[#141821]/95 backdrop-blur-xl border-border/70" data-testid="feed-key-rotate-dialog">
                <AlertDialogHeader>
                  <AlertDialogTitle className="font-display">Regenerate the live-feed key?</AlertDialogTitle>
                  <AlertDialogDescription className="text-muted-foreground leading-relaxed">
                    The current key stops working the moment you confirm. Deliveries from the counting project will be rejected with 401 until it is updated with the new key. Historical data is not affected.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="feed-key-rotate-cancel" className="border-border/70 bg-secondary/60 hover:bg-secondary">Keep current key</AlertDialogCancel>
                  <AlertDialogAction data-testid="feed-key-rotate-confirm" onClick={rotate} className="bg-[#E05A4F] text-white hover:bg-[#c94c42]">Regenerate now</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
          <Callout tone="warn" title="Security notes" testId="brief-security">
            <ul className="list-disc list-inside space-y-1 text-xs">
              <li>Send the key only in the <span className="font-mono">Authorization</span> header (or <span className="font-mono">X-Api-Key</span>), never in the URL.</li>
              <li>The dashboard login does not accept this key, and this key cannot read any data - it can only post counts.</li>
              <li>If the key leaks, regenerate it here and update the sender.</li>
            </ul>
          </Callout>
        </Panel>

        {/* endpoint */}
        <Panel index={1} className="xl:col-span-7" testId="brief-endpoint-panel" title="Endpoint" subtitle="HTTPS, JSON in, JSON out. Health-check with the ping URL first." right={<Link2 size={16} className="text-[#C9A961]" />}>
          <div className="space-y-3">
            <div className="rounded-lg border border-border/60 p-3">
              <div className="flex items-center justify-between gap-2 mb-1">
                <div className="eyebrow text-[10px]">Deliveries</div>
                <CopyButton text={ingestUrl} what="Endpoint URL" testId="endpoint-copy" />
              </div>
              <div className="flex items-center gap-2 font-mono text-[13px] break-all">
                <Badge variant="outline" className="border-[rgba(47,191,143,0.35)] bg-[rgba(47,191,143,0.10)] text-[#2FBF8F] font-mono text-[10px]">POST</Badge>
                <span data-testid="endpoint-url">{ingestUrl}</span>
              </div>
            </div>
            <div className="rounded-lg border border-border/60 p-3">
              <div className="flex items-center justify-between gap-2 mb-1">
                <div className="eyebrow text-[10px]">Connectivity check</div>
                <CopyButton text={pingUrl} what="Ping URL" testId="ping-copy" />
              </div>
              <div className="flex items-center gap-2 font-mono text-[13px] break-all">
                <Badge variant="outline" className="border-[rgba(90,167,224,0.35)] bg-[rgba(90,167,224,0.10)] text-[#9CCBF0] font-mono text-[10px]">GET</Badge>
                <span data-testid="ping-url">{pingUrl}</span>
              </div>
              <div className="text-[11px] text-muted-foreground mt-1">Returns 200 with the list of site ids when the key is accepted; 401 otherwise.</div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="rounded-lg border border-border/60 p-3">
                <div className="eyebrow text-[10px] flex items-center gap-1.5"><ShieldAlert size={11} /> Auth header</div>
                <div className="font-mono text-xs mt-1">Authorization: Bearer &lt;key&gt;</div>
                <div className="text-[11px] text-muted-foreground mt-1">or X-Api-Key: &lt;key&gt;</div>
              </div>
              <div className="rounded-lg border border-border/60 p-3">
                <div className="eyebrow text-[10px] flex items-center gap-1.5"><Clock3 size={11} /> Cadence</div>
                <div className="font-mono text-xs mt-1">every {brief.cadence.interval_minutes} min</div>
                <div className="text-[11px] text-muted-foreground mt-1">silent after {brief.cadence.silent_after_minutes} min · trading {brief.cadence.trading_hours_myt} MYT</div>
              </div>
              <div className="rounded-lg border border-border/60 p-3">
                <div className="eyebrow text-[10px] flex items-center gap-1.5"><FileJson size={11} /> Body</div>
                <div className="font-mono text-xs mt-1">{`{ rows: Row[], dry_run? }`}</div>
                <div className="text-[11px] text-muted-foreground mt-1">max {brief.payload.limits.max_rows_per_delivery} rows per delivery</div>
              </div>
            </div>
            <Callout tone="info">{brief.cadence.note}</Callout>
          </div>
        </Panel>
      </div>

      {/* payload */}
      <Panel index={2} testId="brief-payload-panel" title="Row schema and examples" subtitle="Only site, date and entered are required. Leave a field out when the camera does not capture it - the dashboard then shows 'not captured', never zero." right={<FileJson size={16} className="text-[#C9A961]" />}>
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-4 md:gap-5">
          <div className="xl:col-span-6 overflow-x-auto scroll-thin">
            <Table>
              <TableHeader>
                <TableRow className="border-border/60 hover:bg-transparent">
                  <TableHead className="eyebrow text-[10px]">Field</TableHead>
                  <TableHead className="eyebrow text-[10px]">Type</TableHead>
                  <TableHead className="eyebrow text-[10px]">Required</TableHead>
                  <TableHead className="eyebrow text-[10px]">Notes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {brief.payload.row.map((f) => (
                  <TableRow key={f.field} className="border-border/40" data-testid={`schema-row-${f.field.split(" ")[0]}`}>
                    <TableCell className="font-mono text-xs text-[#F0DFA8] whitespace-nowrap">{f.field}</TableCell>
                    <TableCell className="font-mono text-xs whitespace-nowrap">{f.type}</TableCell>
                    <TableCell>
                      {f.required ? (
                        <Badge variant="outline" className="border-[rgba(201,169,97,0.35)] bg-[rgba(201,169,97,0.12)] text-[#F0DFA8] font-mono text-[10px]">required</Badge>
                      ) : (
                        <Badge variant="outline" className="border-border/70 bg-secondary/60 text-muted-foreground font-mono text-[10px]">optional</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground leading-relaxed">{f.note}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="xl:col-span-6">
            <Tabs defaultValue="minimal">
              <div className="flex items-center justify-between gap-2 mb-2">
                <TabsList className="bg-secondary/70 border border-border/60 h-9" data-testid="example-tabs">
                  <TabsTrigger value="minimal" data-testid="example-tab-minimal" className="text-xs data-[state=active]:bg-[rgba(201,169,97,0.18)] data-[state=active]:text-foreground">Minimal</TabsTrigger>
                  <TabsTrigger value="full" data-testid="example-tab-full" className="text-xs data-[state=active]:bg-[rgba(201,169,97,0.18)] data-[state=active]:text-foreground">With demographics</TabsTrigger>
                  <TabsTrigger value="curl" data-testid="example-tab-curl" className="text-xs data-[state=active]:bg-[rgba(201,169,97,0.18)] data-[state=active]:text-foreground">curl</TabsTrigger>
                </TabsList>
              </div>
              <TabsContent value="minimal" className="mt-0">
                <div className="flex justify-end mb-1"><CopyButton text={exampleMin} what="Example" testId="example-copy-minimal" /></div>
                <Code testId="example-minimal">{exampleMin}</Code>
              </TabsContent>
              <TabsContent value="full" className="mt-0">
                <div className="flex justify-end mb-1"><CopyButton text={exampleFull} what="Example" testId="example-copy-full" /></div>
                <Code testId="example-full">{exampleFull}</Code>
              </TabsContent>
              <TabsContent value="curl" className="mt-0">
                <div className="flex justify-end mb-1"><CopyButton text={curl} what="curl command" testId="example-copy-curl" /></div>
                <Code testId="example-curl">{curl}</Code>
              </TabsContent>
            </Tabs>
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
              {brief.responses.map((r) => (
                <div key={r.status} className="rounded-lg border border-border/60 p-2.5" data-testid={`response-${r.status}`}>
                  <div className={cn("font-mono text-sm font-semibold", r.status < 300 ? "text-[#2FBF8F]" : r.status === 401 ? "text-[#E05A4F]" : "text-[#E0B84A]")}>{r.status}</div>
                  <div className="text-[11px] text-muted-foreground leading-snug mt-0.5">{r.meaning}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Panel>

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-4 md:gap-5">
        {/* sites */}
        <Panel index={3} className="xl:col-span-7" testId="brief-sites-panel" title="Store identifiers" subtitle="Use the id in the first column (aliases are accepted too, case-insensitive). Unknown names are logged below instead of being dropped silently." right={<Store size={16} className="text-[#C9A961]" />}>
          <div className="overflow-x-auto scroll-thin">
            <Table>
              <TableHeader>
                <TableRow className="border-border/60 hover:bg-transparent">
                  <TableHead className="eyebrow text-[10px]">site id</TableHead>
                  <TableHead className="eyebrow text-[10px]">Store</TableHead>
                  <TableHead className="eyebrow text-[10px]">Mall</TableHead>
                  <TableHead className="eyebrow text-[10px]">Aliases</TableHead>
                  <TableHead className="eyebrow text-[10px]">Send from</TableHead>
                  <TableHead className="eyebrow text-[10px] text-right">Feed</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {brief.sites.map((s) => {
                  const fe = feedSites.find((x) => x.id === s.id);
                  return (
                    <TableRow key={s.id} className="border-border/40" data-testid={`site-row-${s.id}`}>
                      <TableCell>
                        <button type="button" onClick={() => copyText(s.id, "Site id")} className="font-mono text-xs text-[#F0DFA8] hover:underline underline-offset-2 focus-gold rounded" data-testid={`site-id-copy-${s.id}`}>
                          {s.id}
                        </button>
                      </TableCell>
                      <TableCell className="text-sm whitespace-nowrap">{s.name}<div className="text-[11px] text-muted-foreground">{s.category}</div></TableCell>
                      <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{s.cluster}</TableCell>
                      <TableCell className="font-mono text-[11px] text-muted-foreground">{s.aliases.slice(0, 3).join(", ")}</TableCell>
                      <TableCell className="whitespace-nowrap" data-testid={`site-sendfrom-${s.id}`}>
                        {s.send_from ? (
                          <><div className="font-mono text-xs text-[#F0DFA8]">{s.send_from}</div><div className="text-[10px] text-muted-foreground">CSV to {s.history_until}</div></>
                        ) : (
                          <><div className="font-mono text-xs">all history</div><div className="text-[10px] text-muted-foreground">full backfill</div></>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {fe?.state === "live" ? (
                          <Badge variant="outline" className="border-[rgba(47,191,143,0.35)] bg-[rgba(47,191,143,0.10)] text-[#2FBF8F] font-mono text-[10px] gap-1.5"><StoreFeedDot entry={fe} /> live</Badge>
                        ) : fe?.state === "silent" && fe.ever_reported ? (
                          <Badge variant="outline" className="border-[rgba(224,184,74,0.4)] bg-[rgba(224,184,74,0.12)] text-[#E0B84A] font-mono text-[10px]" data-testid={`site-feed-silent-${s.id}`}>silent · {fe.silent_minutes} min</Badge>
                        ) : fe?.state === "idle" ? (
                          <Badge variant="outline" className="border-border/70 bg-secondary/60 text-muted-foreground font-mono text-[10px]">idle · closed</Badge>
                        ) : s.has_data ? (
                          <Badge variant="outline" className="border-[rgba(201,169,97,0.35)] bg-[rgba(201,169,97,0.12)] text-[#F0DFA8] font-mono text-[10px]">history on file</Badge>
                        ) : (
                          <Badge variant="outline" className="border-border/70 bg-secondary/60 text-muted-foreground font-mono text-[10px]">awaiting data</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </Panel>

        <Panel index={4} className="xl:col-span-5" testId="brief-rules-panel" title="Rules the dashboard applies" right={<ListChecks size={16} className="text-[#C9A961]" />}>
          <ul className="space-y-2.5">
            {brief.rules.map((r, i) => (
              <li key={i} className="flex gap-2.5 text-sm text-muted-foreground leading-relaxed">
                <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#C9A961] shrink-0" />
                <span>{r}</span>
              </li>
            ))}
          </ul>
          {unmapped.length > 0 && (
            <div className="mt-4" data-testid="brief-unmapped">
              <Callout tone="warn" title="Unknown store names received">
                <ul className="space-y-1 text-xs font-mono">
                  {unmapped.map((u) => (
                    <li key={u.identifier}>{u.identifier} · {u.rows} row(s) · last {fmtTime(u.last_seen)}</li>
                  ))}
                </ul>
                <p className="text-xs mt-2">Rename these in the sender to one of the ids above - rows under unknown names are not stored.</p>
              </Callout>
            </div>
          )}
        </Panel>
      </div>

      {/* delivery log */}
      <Panel
        index={5}
        testId="brief-deliveries-panel"
        title="Delivery log"
        subtitle={status.last_delivery_at ? `${status.deliveries || 0} deliveries so far · last ${fmtTime(status.last_delivery_at)} · ${status.rows_today || 0} rows today` : "No deliveries received yet."}
        right={<History size={16} className="text-[#C9A961]" />}
      >
        {deliveries && deliveries.length > 0 ? (
          <div className="overflow-x-auto scroll-thin">
            <Table>
              <TableHeader>
                <TableRow className="border-border/60 hover:bg-transparent">
                  {["Received", "Stores", "Rows", "Created", "Updated", "Unchanged", "Skipped (CSV)", "Corrected", "Rejected", "From"].map((h) => (
                    <TableHead key={h} className="eyebrow text-[10px] whitespace-nowrap">{h}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {deliveries.map((d) => (
                  <TableRow key={d.id || d.at} className="border-border/40" data-testid="delivery-row">
                    <TableCell className="font-mono text-xs whitespace-nowrap">{fmtTime(d.at)}</TableCell>
                    <TableCell className="text-xs">{(d.site_names || d.sites || []).join(", ") || <span className="text-muted-foreground">—</span>}</TableCell>
                    <TableCell className="font-mono text-xs tabular">{d.received}</TableCell>
                    <TableCell className="font-mono text-xs tabular">{d.created}</TableCell>
                    <TableCell className="font-mono text-xs tabular">{d.updated}</TableCell>
                    <TableCell className="font-mono text-xs tabular text-muted-foreground">{d.unchanged}</TableCell>
                    <TableCell className="font-mono text-xs tabular text-muted-foreground">{d.skipped_history || 0}</TableCell>
                    <TableCell className="font-mono text-xs tabular">{d.corrected_past_days || 0}</TableCell>
                    <TableCell className={cn("font-mono text-xs tabular", d.error_count ? "text-[#E05A4F]" : "text-muted-foreground")}>{d.error_count || 0}{d.unmapped_sites?.length ? ` · ${d.unmapped_sites.length} unknown site` : ""}</TableCell>
                    <TableCell className="font-mono text-[11px] text-muted-foreground">{d.remote || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="rounded-lg border border-dashed border-border/70 p-6 text-sm text-muted-foreground" data-testid="deliveries-empty">
            <div className="flex items-center gap-2 text-foreground mb-1"><RadioTower size={15} className="text-[#C9A961]" /> Waiting for the first delivery</div>
            Post one row with <span className="font-mono">dry_run: true</span> to validate the connection without storing anything - it will show up here as a dry run.
          </div>
        )}
        {status.last_error && (
          <div className="mt-3 text-[11px] font-mono text-[#E0B84A]" data-testid="feed-last-error">Last problem: {status.last_error} · {fmtTime(status.last_error_at)}</div>
        )}
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-border/60 pt-3">
          <div className="text-[11px] font-mono text-muted-foreground" data-testid="import-report">
            <Upload size={11} className="inline mr-1.5 -mt-0.5" />
            Historical CSV exports: {importReport ? `${importReport.files} files · ${importReport.total_rows ?? importReport.rows} store-days on file${importReport.rejected?.length ? ` · ${importReport.rejected.length} file(s) skipped` : ""}` : "import report unavailable"}
          </div>
          <Button variant="ghost" size="sm" data-testid="brief-reimport" disabled={reimporting} onClick={reimport} className="h-8 text-xs gap-1.5 hover:bg-muted/40">
            <RefreshCw size={13} className={reimporting ? "animate-spin" : ""} /> Re-scan CSV folder
          </Button>
        </div>
      </Panel>
    </div>
  );
}
